import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import announcements from '../data/announcements.json';

const AnnouncementPopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [activeAnnouncements, setActiveAnnouncements] = useState<typeof announcements.announcements>([]);

  useEffect(() => {
    const checkAnnouncements = () => {
      const now = new Date().getTime();
      const active = announcements.announcements.filter(announcement => {
        const startDate = new Date(announcement.startDate).getTime();
        const endDate = new Date(announcement.endDate).getTime();
        return now >= startDate && now <= endDate;
      });

      if (active.length > 0) {
        setActiveAnnouncements(active);
        const seenInThisSession = sessionStorage.getItem('announcements_seen');
        if (!seenInThisSession) {
          setIsVisible(true);
          setCurrentAnnouncementIndex(0);
        }
      }
    };

    checkAnnouncements();
  }, []);

  const handleClose = () => {
    sessionStorage.setItem('announcements_seen', 'true');
    setIsVisible(false);
  };

  const handleNext = () => {
    if (currentAnnouncementIndex < activeAnnouncements.length - 1) {
      setCurrentAnnouncementIndex(prev => prev + 1);
    } else {
      handleClose();
    }
  };

  if (!isVisible || activeAnnouncements.length === 0) {
    return null;
  }

  const currentAnnouncement = activeAnnouncements[currentAnnouncementIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative bg-card rounded-lg shadow-lg w-full max-w-[95vw] md:max-w-[80vw] lg:max-w-[800px] mx-auto">
        <button
          onClick={handleClose}
          className="absolute -top-2 -right-2 p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors z-10"
          aria-label="Close announcement"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="p-4 sm:p-6">
          <div className="mb-4 w-full rounded-lg overflow-hidden">
            <div className="relative aspect-square sm:aspect-[4/3] md:aspect-[16/9] lg:aspect-[2/1]">
              <img
                src={currentAnnouncement.image}
                alt="Announcement"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
          
          <h3 className="text-lg sm:text-xl font-semibold mb-2">{currentAnnouncement.title}</h3>
          <p className="text-sm sm:text-base text-muted-foreground mb-4">{currentAnnouncement.message}</p>
          
          <div className="flex items-center justify-between">
            <div className="text-xs sm:text-sm text-muted-foreground">
              <p>Valid from: {new Date(currentAnnouncement.startDate).toLocaleDateString()}</p>
              <p>Valid until: {new Date(currentAnnouncement.endDate).toLocaleDateString()}</p>
            </div>
            
            <div className="flex items-center gap-2">
              {activeAnnouncements.length > 1 && (
                <span className="text-sm text-muted-foreground">
                  {currentAnnouncementIndex + 1} of {activeAnnouncements.length}
                </span>
              )}
              {currentAnnouncementIndex < activeAnnouncements.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup;